import { Coordinate,MapNode  } from '_lib/api';
import { getTopologyType } from '_store/application/hooks';
import applicationReducer from '_store/application/reducer';
import {
  activeLayersSelector,
  countLayersSelector,
  dialogSelector,
  extraSelector,
  mapPositionSelector,
  selectionSelector,
  zoomSelector,
} from '_store/application/selectors';
import { TopologyType } from '_store/application/types';
import { useDenmsForRoadSegment } from '_store/denm/utils';
import { devicesSelector } from '_store/devices/selectors';
import { pmvsSelector } from '_store/pmv/selectors';
import { useIvimsForRoadSegment } from '_store/ivim/utils';
import { Road } from '_store/roads/roadsTypes';
import { roadsSelector, selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { spatemsSelector } from '_store/spatem/selectors';
import { camsSelector } from '_store/vehicles/selectors';
import { Map as LeafletMap } from 'leaflet';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getFromLocalStorage } from '../../../../../utils';
import { calculateCenterCoordinates, findCoordinatesBounds, getBoundsZoomLevel } from '../utils';

export default () => {
  const googleMapsApiKey = process.env.GMAPS_API_KEY;
  const dispatch = useDispatch();

  const roads: Road[] = useSelector(roadsSelector);
  const selectedRoadSegmentIds: string[] = useSelector(selectedRoadSegmentIdsSelector);
  const denms = useDenmsForRoadSegment(selectedRoadSegmentIds);
  const ivim = useIvimsForRoadSegment(selectedRoadSegmentIds);
  const vehicles = useSelector(camsSelector);
  const devices = useSelector(devicesSelector);
  const pmvs = useSelector(pmvsSelector);
  const spatems = useSelector(spatemsSelector);
  const mapPosition = useSelector(mapPositionSelector);
  const zoom = useSelector(zoomSelector);
  const dialog = useSelector(dialogSelector);
  const extra = useSelector(extraSelector);
  const selection = useSelector(selectionSelector);
  const activeLayers = useSelector(activeLayersSelector);
  const countLayers = useSelector(countLayersSelector);

  const links: Record<number, MapNode[]> = {};
  // if (nodes) {
  //   [...nodes]
  //     .sort((a, b) => a.number - b.number)
  //     .forEach((n) => {
  //       const linkdir = `${n.link}-${n.direction}`;
  //       if (linkdir in links) {
  //         links[linkdir].push(n);
  //       } else {
  //         links[linkdir] = [n];
  //       }
  //     });
  // }

  const [mapRef, setMapRef] = useState<LeafletMap>(null);
  /** Increments when the map container is resized (e.g. dashboard panel collapse); used to re-create heat layer. */
  const [mapResizeKey, setMapResizeKey] = useState(0);

  const [loading, setLoading] = useState(true);
  const [centerCoordinates, setCenterCoordinates] = useState(undefined);

  const loadHandler = useCallback(
    (map: LeafletMap) => {
      // Store a reference to the map instance in state
      if (!mapRef) {
        map.on('zoom', (e) => {
          dispatch(applicationReducer.actions.updateZoom(e.target.getZoom()));
        });
        setMapRef(map);
        dispatch(applicationReducer.actions.setMapRef(map));
      }

      const isUrbanTopology = getTopologyType() === TopologyType.URBAN;
      const layers = ['event', 'informative', 'rsu', 'pmv', ...(isUrbanTopology ? ['spatem'] : []), 'data'];

      layers.forEach((layer) => {
        dispatch(
          applicationReducer.actions.updateActiveLayer({
            layer,
            status: getFromLocalStorage(layer) ?? true,
          }),
        );
      });
    },
    [mapRef],
  );

  const meterPerPixel = useCallback(
    () =>
      (156543.03392 * Math.cos((mapPosition.center.lat * Math.PI) / 180)) / 2 ** mapRef.getZoom(),
    [mapPosition, mapRef],
  );

  useEffect(() => {
    if (selectedRoadSegmentIds.length > 0 && mapRef) {
      const groupOfCoordinates = selectedRoadSegmentIds.flatMap(
        (id) => roads.find((r) => r.id === id)?.waypoints.coordinates || [],
      );

      if (groupOfCoordinates.length > 0) {
        const coordinates: Coordinate[] = groupOfCoordinates.flatMap((line) => line);

        if (coordinates.length > 0) {
          const bounds = findCoordinatesBounds(coordinates);
          const newZoomLevel = getBoundsZoomLevel(bounds, mapRef);
          dispatch(applicationReducer.actions.updateZoom(newZoomLevel));
          mapRef.setZoom(newZoomLevel);
          mapRef.flyToBounds(bounds, {
            animate: true,
            duration: 1.5,
          });
        }
      }
    }
  }, [selectedRoadSegmentIds, mapRef, mapPosition]);

  useEffect(() => {
    setCenterCoordinates(
      calculateCenterCoordinates(roads, selectedRoadSegmentIds, mapPosition.center),
    );
  }, [roads, selectedRoadSegmentIds, mapPosition, mapRef]);

  useEffect(() => {
    if (centerCoordinates) {
      setLoading(false);
    }
  }, [centerCoordinates]);

  useEffect(() => {
    if (mapRef) {
      mapRef.invalidateSize();
    }
  }, [dialog]);

  // When the map container is resized (e.g. heatmap dashboard panel collapse/expand), invalidate
  // size so Leaflet loads new tiles and layers (including heat) use the correct dimensions.
  useEffect(() => {
    if (!mapRef) return undefined;
    const container = mapRef.getContainer();
    let debounceTimer: ReturnType<typeof setTimeout>;
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          mapRef.invalidateSize();
          setMapResizeKey((k) => k + 1);
        });
      }, 100);
    });
    resizeObserver.observe(container);
    return () => {
      clearTimeout(debounceTimer);
      resizeObserver.disconnect();
    };
  }, [mapRef]);

  return {
    roads,
    devices,
    pmvs,
    denms,
    ivim,
    vehicles,
    spatems,
    mapPosition,
    dialog,
    extra,
    selection,
    activeLayers,
    countLayers,
    mapRef,
    mapResizeKey,
    loading,
    links,
    loadHandler,
    meterPerPixel,
    centerCoordinates,
    googleMapsApiKey,
    zoom,
  };
};
